import { makeAutoObservable, runInAction, reaction } from 'mobx'
import * as Sentry from '@sentry/react'
import {
  getFirestore,
  doc,
  deleteDoc,
  collection,
  getDocs,
  query,
} from 'firebase/firestore'
import { getAuth, deleteUser } from 'firebase/auth'
import { getStorage, ref, deleteObject, listAll } from 'firebase/storage'
import {
  getDocument,
  setDocument,
  updateDocument,
} from '../../../shared/api/firestore'
import FirebaseApp from '../../../shared/api/firebase'

const db = getFirestore(FirebaseApp)
const auth = getAuth(FirebaseApp)
const storage = getStorage(FirebaseApp)

class UserStore {
  user = null
  isLoading = true

  constructor(rootStore) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true })
    this.rootStore = rootStore
    this.authStore = rootStore.authStore
    this.organizationStore = rootStore.organizationStore
    this.errorStore = rootStore.errorStore

    reaction(
      () => this.authStore.currentUser?.uid,
      (uid) => {
        if (uid) {
          this.loadUserAndOrganization(uid)
        } else {
          this.clearUserData()
        }
      },
    )
  }

  clearUserData() {
    this.user = null
    this.organizationStore.clearOrganizationData()
    if (this.rootStore.mapStore) {
      this.rootStore.mapStore.clearCache()
    }
  }

  async _createUserDocumentOnTheFly(userId) {
    if (!this.authStore.currentUser) return null
    console.warn(`Документ для пользователя ${userId} не найден. Создаем...`)
    const { email, displayName } = this.authStore.currentUser
    const newUserDocData = {
      email: email,
      name: displayName || '',
      roles: ['owner'], // Сразу назначаем роль владельца
      organizationIds: [],
      settings: {
        showTourOnLogin: true, // Показывать тур при входе по умолчанию
      },
    }
    await setDocument('users', userId, newUserDocData)
    return newUserDocData
  }

  async _findAndLoadOrganization(userDoc) {
    if (!userDoc?.organizationIds?.length) {
      console.log('Новый пользователь или пользователь без организации.')
      this.organizationStore.clearOrganizationData()
      return
    }

    const orgId = userDoc.organizationIds[0]

    if (orgId) {
      await this.organizationStore.fetchOrganization(orgId)
    } else {
      console.warn('Пользователь не привязан к организации.')
      this.organizationStore.clearOrganizationData()
    }
  }

  async loadUserAndOrganization(userId) {
    this.isLoading = true
    try {
      let userDoc = await getDocument('users', userId)

      if (!userDoc) {
        userDoc = await this._createUserDocumentOnTheFly(userId)
      }

      runInAction(() => {
        this.user = userDoc
      })

      await this._findAndLoadOrganization(userDoc)
    } catch (error) {
      console.error(
        'Не удалось загрузить данные пользователя и организации',
        error,
      )
      Sentry.captureException(error, {
        extra: { context: 'loadUserAndOrganization', userId },
      })
      this.errorStore.showError('Не удалось загрузить данные организации')
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Обновляет настройки пользователя в Firestore.
   * @param {object} settings - Объект с настройками для обновления.
   */
  async updateUserSettings(settings) {
    if (!this.user?.id) return

    try {
      // Используем dot-нотацию для обновления вложенных полей
      const updateData = {}
      Object.keys(settings).forEach((key) => {
        updateData[`settings.${key}`] = settings[key]
      })
      await updateDocument('users', this.user.id, updateData)
      runInAction(() => {
        this.user.settings = { ...this.user.settings, ...settings }
      })
    } catch (error) {
      console.error('Не удалось обновить настройки пользователя:', error)
      this.errorStore.showError('Не удалось сохранить настройки.')
    }
  }
  async deleteUserAccount() {
    this.isLoading = true
    try {
      const currentUser = auth.currentUser
      if (!currentUser) {
        this.errorStore.showError('Пользователь не авторизован.')
        return
      }

      const userId = currentUser.uid
      const organizationId = this.organizationStore.organization?.id
      const isOwner = this.user?.roles?.includes('owner')

      // 1. Удаление данных организации (если пользователь - владелец)
      if (isOwner && organizationId) {
        // Удаление подколлекций (staff, clients, services, bookings)
        const collectionsToDelete = ['staff', 'clients', 'services', 'bookings']
        for (const colName of collectionsToDelete) {
          const q = query(
            collection(db, 'organizations', organizationId, colName),
          )
          const querySnapshot = await getDocs(q)
          for (const docSnapshot of querySnapshot.docs) {
            await deleteDoc(
              doc(db, 'organizations', organizationId, colName, docSnapshot.id),
            )
          }
        }

        // Удаление изображений организации из Storage
        const orgImagesRef = ref(storage, `organizations/${organizationId}`)
        const res = await listAll(orgImagesRef)
        for (const itemRef of res.items) {
          await deleteObject(itemRef)
        }

        // Удаление документа организации
        await deleteDoc(doc(db, 'organizations', organizationId))
      }

      // 2. Удаление документа пользователя из Firestore
      await deleteDoc(doc(db, 'users', userId))

      // 3. Удаление пользователя из Firebase Authentication
      await deleteUser(currentUser)

      this.errorStore.showSuccess(
        'Аккаунт и все связанные данные успешно удалены.',
      )
      this.authStore.logout()
    } catch (error) {
      console.error('Ошибка при удалении аккаунта:', error)
      // Firebase Auth deleteUser требует недавней аутентификации.
      // Если пользователь давно не входил, может потребоваться re-authenticate.
      if (error.code === 'auth/requires-recent-login') {
        this.errorStore.showError(
          'Для удаления аккаунта необходимо повторно войти в систему.',
        )
      } else {
        this.errorStore.showError('Не удалось удалить аккаунт.')
      }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }
}

export default UserStore
